import api from './axios';

// Item interface
export interface Item {
  id: number;
  value: string; // item name
  tooltip: string;
  name_id: number;
  tooltip_id: number;
  socket: number;
  wear_type: number;
  group: number;
  type: number;
  icon_file_name: string;
  grade: number;
  rank: number;
  class: number;
}

// Gem interface
export interface Gem {
  id: number;
  name: string;
  tooltip: string;
  icon_file_name: string;
}

// Item response interface
export interface ItemsResponse {
  page: number;
  size: number;
  total: number;
  items: Item[];
}

// Gems response interface
export interface GemsResponse {
  total: number;
  gems: Gem[];
}

// Item search params interface
export interface ItemSearchParams {
  page?: number;
  size?: number;
  search?: string;
  type?: string;
  rank?: string;
  grade?: string;
  language?: string;
}

// Item command params interface
export interface ItemCommandParams {
  itemId: number;
  quantity: number;
  level?: number;
  enchant?: number;
  tamed?: boolean;
  socket1?: number;
  socket2?: number;
  socket3?: number;
  socket4?: number;
  itemType: string;
  characterId: number;
  characterName: string;
}

// Command response interface
export interface CommandResponse {
  message: string;
  command: string;
}

/**
 * Get items with pagination, search, and filters
 * @param params Search parameters
 * @returns Items response
 */
export const getItems = async (params: ItemSearchParams = {}): Promise<ItemsResponse> => {
  // Define query parameters
  const queryParams = new URLSearchParams();
  
  // Add parameters to query
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.size) queryParams.append('size', params.size.toString());
  if (params.search) queryParams.append('search', params.search);
  if (params.type) queryParams.append('type', params.type);
  if (params.rank) queryParams.append('rank', params.rank);
  if (params.grade) queryParams.append('grade', params.grade);
  if (params.language) queryParams.append('language', params.language);
  
  // Make API call
  const response = await api.get<ItemsResponse>(`/items?${queryParams.toString()}`);
  return response.data;
};

/**
 * Get all gems for item sockets
 * @param language Language for string resources
 * @returns Gems response
 */
export const getGems = async (language: string = 'ru'): Promise<GemsResponse> => {
  const queryParams = new URLSearchParams();
  if (language) queryParams.append('language', language);
  
  const response = await api.get<GemsResponse>(`/items/gems?${queryParams.toString()}`);
  return response.data;
};

/**
 * Add an item to a character
 * @param params Item parameters
 * @returns Command response
 */
export const addItem = async (params: ItemCommandParams): Promise<CommandResponse> => {
  const response = await api.post<CommandResponse>('/items/add', params);
  return response.data;
};

/**
 * Remove an item from a character
 * @param params Item parameters
 * @returns Command response
 */
export const removeItem = async (params: Pick<ItemCommandParams, 'itemId' | 'quantity' | 'characterId' | 'characterName'>): Promise<CommandResponse> => {
  const response = await api.post<CommandResponse>('/items/remove', params);
  return response.data;
}; 